import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase, readableError } from '../../lib/supabase'
import { Shell } from '../../components/Shell'
import { Button, Spinner, mmss } from '../../components/ui'

/**
 * Tap the words in time with the song.
 *
 * The Collective's lyric card has to know when each line lands. Transcription
 * is the obvious answer and the wrong one: speech recognition is trained on
 * speech and does poorly on sung vowels over a guitar, and in any case we are
 * not missing the words — we have them. The missing thing is only *when*, and
 * the person who knows that already wrote the song.
 *
 * So: play it once, tap as each line comes round, done in the length of the
 * song. More accurate than an aligner, free, offline, and it hands no
 * recording to a third party.
 *
 * The screen is one control. The whole panel is the tap target, because this
 * is done one-handed with the phone at arm's length while listening — hunting
 * for a button is exactly the moment the timing is lost. Everything else here
 * is either the line you are marking or a way to undo the last one.
 */
export default function SyncLyrics() {
  const { id } = useParams()
  const nav = useNavigate()

  const [song, setSong] = useState(null)
  const [lines, setLines] = useState([])
  const [cues, setCues] = useState([])      // seconds, one per marked line
  const [src, setSrc] = useState(null)
  const [playing, setPlaying] = useState(false)
  const [pos, setPos] = useState(0)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  const audio = useRef(null)

  useEffect(() => {
    (async () => {
      const [songRes, secRes] = await Promise.all([
        supabase.from('songs').select('id, title, duration_seconds, lyric_cues, r2_object_key')
          .eq('id', id).maybeSingle(),
        supabase.from('song_sections').select('lyrics, order_position')
          .eq('song_id', id).order('order_position'),
      ])
      if (songRes.error) return setErr(readableError(songRes.error))
      if (!songRes.data) return setErr('That song is no longer here.')
      setSong(songRes.data)

      const all = (secRes.data ?? []).flatMap(
        (s) => (s.lyrics || '').split('\n').map((l) => l.trim()).filter(Boolean))
      setLines(all)

      // Re-syncing starts from the existing cues only if they still describe
      // these words. Lyrics edited since the last sync mean the old times point
      // at lines that have moved, so the honest move is to start over.
      const saved = songRes.data.lyric_cues
      if (saved?.lines?.length && saved.lines.every((c, i) => c.text === all[i])) {
        setCues(saved.lines.map((c) => c.at))
      }

      const { data, error } = await supabase.functions
        .invoke('r2-playback-url', { body: { song_id: id } })
      if (error) return setErr(readableError(error))
      setSrc(data?.url ?? null)
    })()
  }, [id])

  const done = cues.length >= lines.length && lines.length > 0

  /**
   * Mark the line that is coming up now.
   *
   * The time recorded is the audio's own currentTime, never a wall clock —
   * a dropped frame or a paused tab would otherwise skew every later cue.
   */
  const mark = useCallback(() => {
    const a = audio.current
    if (!a || done) return
    if (a.paused) {
      // A tap that starts playback must say so if it can't. Swallowing this
      // leaves someone tapping a panel that will never record anything and no
      // way to tell whether the app or their timing is at fault.
      a.play().catch(() => setErr(
        "That recording wouldn't play here. Try again, or open this song's "
        + 'recording first to check it plays.',
      ))
      return
    }
    setCues((c) => [...c, +a.currentTime.toFixed(2)])
  }, [done])

  const undo = () => setCues((c) => c.slice(0, -1))

  const restart = () => {
    const a = audio.current
    setCues([])
    if (a) { a.currentTime = 0; a.play().catch(() => {}) }
  }

  // Space and Enter do what the panel does, so this is usable at a desk with
  // the song playing out loud — the same job, one hand on the keyboard.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== ' ' && e.key !== 'Enter') return
      if (e.target.closest('button')) return   // let a real button be pressed
      e.preventDefault()
      mark()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mark])

  const save = async () => {
    setSaving(true); setErr(null)
    const payload = {
      version: 1,
      lines: cues.map((at, i) => ({ text: lines[i], at })),
    }
    const { error } = await supabase.from('songs')
      .update({ lyric_cues: payload, lyric_cues_set_at: new Date().toISOString() })
      .eq('id', id)
    setSaving(false)
    if (error) return setErr(readableError(error))
    nav(`/songs/${id}/share`)
  }

  const clear = async () => {
    setSaving(true); setErr(null)
    const { error } = await supabase.from('songs')
      .update({ lyric_cues: null, lyric_cues_set_at: null }).eq('id', id)
    setSaving(false)
    if (error) return setErr(readableError(error))
    setCues([])
  }

  if (err && !song) return (
    <Shell back={`/songs/${id}/share`} title="Sharing" hidePlayer>
      <h2 className="q">That didn't open.</h2>
      <p className="sub">{err}</p>
    </Shell>
  )
  if (!song) return <Shell hidePlayer><Spinner /></Shell>

  const at = cues.length                    // the line being marked next
  const upcoming = lines.slice(at + 1, at + 3)

  if (!lines.length) return (
    <Shell back={`/songs/${id}/share`} title="Sharing" hidePlayer>
      <div className="eyebrow">Sync the words</div>
      <h2 className="q">There are no lyrics yet.</h2>
      <p className="sub">
        Add the words to {song.title || 'this song'} first — the sync marks
        lines you have already written, it doesn't transcribe them.
      </p>
      <button className="btn btn-quiet" onClick={() => nav(`/songs/${id}/sheet`)}>
        Write the words
      </button>
    </Shell>
  )

  if (!song.r2_object_key) return (
    <Shell back={`/songs/${id}/share`} title="Sharing" hidePlayer>
      <div className="eyebrow">Sync the words</div>
      <h2 className="q">There's no recording to tap along to.</h2>
      <p className="sub">
        Add a recording of {song.title || 'this song'} and the words can be
        timed against it.
      </p>
    </Shell>
  )

  return (
    <Shell back={`/songs/${id}/share`} title="Sharing" hidePlayer>
      <div className="eyebrow">Sync the words</div>
      {/* The instructions are worth the room until the first tap and dead
          weight after it — by then the panel has explained itself, and the
          space is better spent on the line being marked and on the save that
          would otherwise sit below the fold. */}
      {at === 0 ? (
        <>
          <h2 className="q">Tap as each line starts.</h2>
          <p className="sub">
            Play it and tap the panel the moment each line begins. This is what
            the Collective uses to hold the words in time — you only do it once.
          </p>
        </>
      ) : (
        <h2 className="q sync-lean">{song.title || 'Untitled'}</h2>
      )}

      <audio ref={audio} src={src ?? undefined} preload="metadata"
             onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
             onTimeUpdate={(e) => setPos(e.target.currentTime)}
             onEnded={() => setPlaying(false)} />

      <div className="sync-meter" aria-hidden="true">
        <i style={{ width: `${lines.length ? (at / lines.length) * 100 : 0}%` }} />
      </div>
      <div className="sync-count">
        <span>{at} of {lines.length} lines</span>
        <span className="sync-time">{mmss(pos)}</span>
      </div>

      {/* One target, the size of the panel. The button element is the panel
          rather than something sitting on it, so there is no near-miss. */}
      <button type="button" className={`sync-pad${done ? ' is-done' : ''}`}
              onClick={mark} disabled={done}
              aria-label={playing ? `Mark the line: ${lines[at] ?? ''}` : 'Play and start marking'}>
        {done ? (
          <>
            <span className="sync-hint">Every line is marked</span>
            <span className="sync-now">That's the whole song.</span>
          </>
        ) : (
          <>
            <span className="sync-hint">
              {playing ? 'Tap when this line starts' : 'Tap to start playing'}
            </span>
            <span className="sync-now">{lines[at]}</span>
            {upcoming.map((l, i) => (
              <span key={i} className={`sync-next${i ? ' is-later' : ''}`}>{l}</span>
            ))}
          </>
        )}
      </button>

      <div className="sync-controls">
        <button type="button" className="btn-plain" onClick={undo} disabled={!at}>
          Undo last
        </button>
        <button type="button" className="btn-plain" onClick={restart} disabled={!at}>
          Start again
        </button>
      </div>

      {err && <p className="err">{err}</p>}

      <Button onClick={save} busy={saving} disabled={!at}
              style={{ marginTop: 14, width: '100%' }}>
        {done ? 'Save the timing' : `Save ${at} of ${lines.length}`}
      </Button>
      {at > 0 && !done && (
        <p className="small" style={{ marginTop: 8 }}>
          A partial sync is fine — the Collective shows the {at} line
          {at === 1 ? '' : 's'} you marked, in time, and stops there. Come back
          and mark the rest whenever.
        </p>
      )}
      {song.lyric_cues && (
        <button type="button" className="btn-plain" style={{ marginTop: 12 }}
                onClick={clear} disabled={saving}>
          Remove the saved timing
        </button>
      )}
    </Shell>
  )
}
